/**
 * Library Upload Page
 * 
 * Phase C4 - Upload flow with required metadata
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Button, Card, Select } from '@threadlock/ui';
import { ROUTES } from '@threadlock/routing';
import { createDocument, createJob, type CreateDocumentInput, type CreateJobInput, type DocumentContentType } from '@threadlock/data';
import { db } from '../../firebase-init';
import { useWorkspaceState } from '../../config/workspace';
import { useAuthState } from '../../config/auth';
import { getStorage, ref, uploadBytes } from 'firebase/storage';

// Placeholder enums - in a real implementation, these would come from a config
const DOCUMENT_TYPES = [
  { value: 'policy', label: 'Policy Document' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'guideline', label: 'Guideline' },
  { value: 'template', label: 'Template' },
  { value: 'other', label: 'Other' },
];

const JURISDICTIONS = [
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'local', label: 'Local' },
  { value: 'international', label: 'International' },
];

const POLICY_TIERS = [
  { value: 'tier1', label: 'Tier 1 - Critical' },
  { value: 'tier2', label: 'Tier 2 - Important' },
  { value: 'tier3', label: 'Tier 3 - Standard' },
];

export default function LibraryUploadPage() {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspaceState();
  const { user } = useAuthState();
  const userId = user?.uid;
  
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [policyTier, setPolicyTier] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = file && documentType && jurisdiction && policyTier;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    
    if (!validTypes.includes(selectedFile.type)) {
      setError('Only PDF and DOCX files are supported');
      setFile(null);
      return;
    }

    setError(null);
    setFile(selectedFile);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid || !activeWorkspaceId || !userId) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Generate document ID
      const documentId = `doc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      // Storage path: rb/{workspaceId}/documents/{documentId}/raw/{filename}
      const storagePath = `rb/${activeWorkspaceId}/documents/${documentId}/raw/${file.name}`;
      
      // Upload file to Storage
      const storage = getStorage();
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, file);

      // Create document record
      const documentInput: CreateDocumentInput = {
        workspaceId: activeWorkspaceId,
        filename: file.name,
        storagePath,
        contentType: file.type as DocumentContentType,
        documentType,
        jurisdiction,
        policyTier,
        createdByUserId: userId,
      };
      
      await createDocument(db, documentId, documentInput);

      // Create upload_initiated job
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const jobInput: CreateJobInput = {
        workspaceId: activeWorkspaceId,
        type: 'ingestion',
        action: 'upload_initiated',
        documentId,
        status: 'ok',
        message: 'Document uploaded successfully',
        meta: {
          contentType: file.type,
          filename: file.name,
        },
        createdByUserId: userId,
      };
      
      await createJob(db, jobId, jobInput);

      // Navigate to library list
      navigate(ROUTES.LIBRARY);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Upload failed. Please try again.');
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title="Upload Document"
        description="Add a new document to your knowledge base"
        actions={
          <Button variant="secondary" onClick={() => navigate(ROUTES.LIBRARY)}>
            Cancel
          </Button>
        }
      />

      <Card className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-[var(--text-sm)] font-medium text-[var(--surface-text-primary)] mb-2">
              Document File <span className="text-[var(--status-error)]">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileChange}
              disabled={uploading}
              className="block w-full text-[var(--text-sm)] text-[var(--surface-text-primary)]
                file:mr-4 file:py-2 file:px-4
                file:rounded-[var(--radius-md)]
                file:border-0
                file:text-[var(--text-sm)] file:font-semibold
                file:bg-[var(--brand-primary)]
                file:text-white
                hover:file:bg-[var(--brand-primary-hover)]
                file:cursor-pointer
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-2 text-[var(--text-xs)] text-[var(--surface-text-muted)]">
              Supported formats: PDF, DOCX
            </p>
          </div>

          {/* Document Type */}
          <div>
            <label htmlFor="documentType" className="block text-[var(--text-sm)] font-medium text-[var(--surface-text-primary)] mb-2">
              Document Type <span className="text-[var(--status-error)]">*</span>
            </label>
            <Select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              disabled={uploading}
              required
            >
              <option value="">Select document type...</option>
              {DOCUMENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Jurisdiction */}
          <div>
            <label htmlFor="jurisdiction" className="block text-[var(--text-sm)] font-medium text-[var(--surface-text-primary)] mb-2">
              Jurisdiction <span className="text-[var(--status-error)]">*</span>
            </label>
            <Select
              id="jurisdiction"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              disabled={uploading}
              required
            >
              <option value="">Select jurisdiction...</option>
              {JURISDICTIONS.map((j) => (
                <option key={j.value} value={j.value}>
                  {j.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Policy Tier */}
          <div>
            <label htmlFor="policyTier" className="block text-[var(--text-sm)] font-medium text-[var(--surface-text-primary)] mb-2">
              Policy Tier <span className="text-[var(--status-error)]">*</span>
            </label>
            <Select
              id="policyTier"
              value={policyTier}
              onChange={(e) => setPolicyTier(e.target.value)}
              disabled={uploading}
              required
            >
              <option value="">Select policy tier...</option>
              {POLICY_TIERS.map((tier) => (
                <option key={tier.value} value={tier.value}>
                  {tier.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-[var(--text-sm)] text-[var(--status-error)] bg-[var(--status-error-bg)] p-3 rounded-[var(--radius-md)]">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isValid || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
